import {
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  updateProfile,
  sendPasswordResetEmail,
  confirmPasswordReset as firebaseConfirmPasswordReset,
  type User as FirebaseUser,
  type UserCredential,
} from 'firebase/auth';
import { auth } from './config';
import { AuthContext } from './AuthContext';
import { useAppStore } from '@/store';
import type { User } from '@/store/types';
import type { AuthContextValue } from './types';
import { setTokenGetter, setUnauthorizedCallback } from '@/lib/api';

const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiration

// Google Auth Provider instance
const googleProvider = new GoogleAuthProvider();

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Maps a Firebase user to the application's User type
 */
function mapFirebaseUserToUser(firebaseUser: FirebaseUser): User {
  return {
    id: firebaseUser.uid,
    email: firebaseUser.email || '',
    name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
    role: 'teacher', // Default role; can be extended with custom claims
  };
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const tokenRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Zustand store actions
  const login = useAppStore((state) => state.login);
  const logout = useAppStore((state) => state.logout);
  const setLoading = useAppStore((state) => state.setLoading);
  const setError = useAppStore((state) => state.setError);

  /**
   * Clears any existing token refresh timer
   */
  const clearTokenRefreshTimer = useCallback(() => {
    if (tokenRefreshTimerRef.current) {
      clearTimeout(tokenRefreshTimerRef.current);
      tokenRefreshTimerRef.current = null;
    }
  }, []);

  /**
   * Refreshes the JWT token
   */
  const refreshToken = useCallback(async (): Promise<string | null> => {
    if (!firebaseUser) return null;

    try {
      const newToken = await firebaseUser.getIdToken(true);
      setToken(newToken);
      return newToken;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return null;
    }
  }, [firebaseUser]);

  /**
   * Schedules automatic token refresh before expiration
   */
  const scheduleTokenRefresh = useCallback(
    async (user: FirebaseUser) => {
      clearTokenRefreshTimer();

      try {
        const tokenResult = await user.getIdTokenResult();
        const expirationTime = new Date(tokenResult.expirationTime).getTime();
        const now = Date.now();
        const timeUntilExpiration = expirationTime - now;

        // Schedule refresh TOKEN_REFRESH_BUFFER_MS before expiration
        const refreshTime = Math.max(
          timeUntilExpiration - TOKEN_REFRESH_BUFFER_MS,
          0
        );

        if (refreshTime > 0) {
          tokenRefreshTimerRef.current = setTimeout(async () => {
            const newToken = await refreshToken();
            if (newToken && firebaseUser) {
              scheduleTokenRefresh(firebaseUser);
            } else {
              // Token refresh failed, trigger logout
              await firebaseSignOut(auth);
            }
          }, refreshTime);
        } else {
          // Token is already expired or about to expire, refresh immediately
          const newToken = await refreshToken();
          if (!newToken) {
            await firebaseSignOut(auth);
          }
        }
      } catch (error) {
        console.error('Failed to schedule token refresh:', error);
      }
    },
    [clearTokenRefreshTimer, refreshToken, firebaseUser]
  );

  /**
   * Sign in with email and password
   * Returns the UserCredential for accessing the user's UID
   */
  const signInWithEmail = useCallback(
    async (email: string, password: string) => {
      setLoading(true);
      setError(null);

      try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        return userCredential;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to sign in';
        setError(message);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setError]
  );

  /**
   * Sign in with Google
   * Returns the UserCredential for accessing the user's UID
   */
  const signInWithGoogle = useCallback(
    async (): Promise<UserCredential> => {
      setLoading(true);
      setError(null);

      try {
        const userCredential = await signInWithPopup(auth, googleProvider);
        return userCredential;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to sign in with Google';
        setError(message);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setError]
  );

  /**
   * Sign up with email and password
   * Returns the UserCredential so caller can access user.uid for Stripe integration
   * Note: Subscription is created by Stripe webhook, not here
   */
  const signUpWithEmail = useCallback(
    async (email: string, password: string, name: string): Promise<UserCredential> => {
      setLoading(true);
      setError(null);

      try {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );

        // Update the user's display name
        await updateProfile(userCredential.user, { displayName: name });

        // Return credential so caller can access user.uid for Stripe
        return userCredential;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to sign up';
        setError(message);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setError]
  );

  /**
   * Sign out the current user
   */
  const signOut = useCallback(async (): Promise<void> => {
    try {
      await firebaseSignOut(auth);
      clearTokenRefreshTimer();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to sign out';
      setError(message);
      throw error;
    }
  }, [clearTokenRefreshTimer, setError]);

  /**
   * Send password reset email
   */
  const sendPasswordReset = useCallback(
    async (email: string): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        await sendPasswordResetEmail(auth, email);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to send password reset email';
        setError(message);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setError]
  );

  /**
   * Confirm password reset with new password
   */
  const confirmPasswordReset = useCallback(
    async (oobCode: string, newPassword: string): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        await firebaseConfirmPasswordReset(auth, oobCode, newPassword);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to reset password';
        setError(message);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setError]
  );

  /**
   * Configure the API client with the token getter and unauthorized callback
   */
  useEffect(() => {
    // Set the token getter for the API client
    setTokenGetter(async () => {
      if (firebaseUser) {
        // Get a fresh token if possible
        try {
          return await firebaseUser.getIdToken();
        } catch {
          return token;
        }
      }
      return token;
    });

    // Set the unauthorized callback to trigger signout
    setUnauthorizedCallback(() => {
      signOut();
    });
  }, [firebaseUser, token, signOut]);

  /**
   * Listen for Firebase auth state changes
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);

      if (user) {
        // User signed in
        const appUser = mapFirebaseUserToUser(user);
        login(appUser);

        // Get and store the token
        try {
          const idToken = await user.getIdToken();
          setToken(idToken);

          // Schedule token refresh
          scheduleTokenRefresh(user);
        } catch (error) {
          console.error('Failed to get token:', error);
        }
      } else {
        // User signed out
        logout();
        setToken(null);
        clearTokenRefreshTimer();
      }

      if (!isInitialized) {
        setIsInitialized(true);
      }
    });

    return () => {
      unsubscribe();
      clearTokenRefreshTimer();
    };
  }, [login, logout, clearTokenRefreshTimer, scheduleTokenRefresh, isInitialized]);

  const value: AuthContextValue = {
    firebaseUser,
    token,
    isInitialized,
    signInWithEmail,
    signInWithGoogle,
    signUpWithEmail,
    signOut,
    refreshToken,
    sendPasswordReset,
    confirmPasswordReset,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
